import { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import { useCalculator } from '@/hooks/useCalculator';
import ResultsTabs from '@/sections/ResultsTabs';
import DosingPopup from '@/sections/DosingPopup';
import { useAuth } from '@/hooks/useAuth';
import { useRequireAuth } from '@/hooks/useRequireAuth';
import { getRetentionPolicy, isWithinRetention, retentionToastMessage, adminOverridePrompt } from '@/lib/retention';
import { AVAILABLE_CHEMICALS } from '@/types/calculator';
import { ALL_STANDARDS } from '@/types/standards';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  AreaChart, Area
} from 'recharts';
import {
  Play, Save, Beaker, Users, RotateCcw, CheckCircle2, AlertTriangle,
  TrendingUp, TrendingDown, Minus, Droplets, Gauge, Thermometer,
  FlaskConical, Atom, ShieldAlert, Activity, Wind, Factory,
  Milk, Beer, Zap, ArrowRight, Trash2, Search, Building2,
  ChevronRight, X, FileBarChart, Layers, Target, Sparkles, Info
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';

/* ─── Types ─── */
interface Customer { id: number; name: string; company?: string | null; }
interface CustomerSample {
  id: number; customerId: number; sampleName: string;
  params: Record<string, number>; standard?: string | null;
  createdAt: string; assessmentResult?: Record<string, unknown> | null;
}

/* ─── localStorage helpers ─── */
function getLocalCustomers(): Customer[] {
  try {
    const raw = localStorage.getItem('rb_customers');
    if (!raw || raw === '[]') return [];
    return JSON.parse(raw).map((c: Customer) => ({ ...c, createdAt: new Date(c.createdAt) }));
  } catch { return []; }
}
function getLocalCustSamples(): CustomerSample[] {
  try {
    const raw = localStorage.getItem('rb_customer_samples');
    if (!raw || raw === '[]') return [];
    return JSON.parse(raw).map((s: CustomerSample) => ({ ...s, createdAt: new Date(s.createdAt) }));
  } catch { return []; }
}
function setLocalCustSamples(samples: CustomerSample[]) {
  localStorage.setItem('rb_customer_samples', JSON.stringify(samples));
}
function genLocalId(arr: { id: number }[]) {
  return arr.length > 0 ? Math.max(...arr.map(x => x.id)) + 1 : 1;
}

/* ─── Category config ─── */
const CATEGORIES = [
  { key: 'basic', label: 'Basic & Flow', icon: Gauge, color: '#22d3ee', params: [
    { key: 'flowRate', label: 'Flow Rate', unit: 'm³/d', step: 0.1, hint: 'Daily flow volume' },
    { key: 'ph', label: 'pH', unit: '', step: 0.01, hint: 'Acidity / alkalinity' },
    { key: 'temperature', label: 'Temperature', unit: '°C', step: 0.1, hint: 'Water temp' },
    { key: 'conductivity', label: 'Conductivity', unit: 'µS/cm', step: 1, hint: 'Electrical conductivity' },
    { key: 'tds', label: 'TDS', unit: 'mg/L', step: 1, hint: 'Total dissolved solids' },
  ]},
  { key: 'organic', label: 'Organic Load', icon: Droplets, color: '#34d399', params: [
    { key: 'bod5', label: 'BOD₅', unit: 'mg/L', step: 1, hint: '5-day biochemical oxygen demand' },
    { key: 'cod', label: 'COD', unit: 'mg/L', step: 1, hint: 'Chemical oxygen demand' },
    { key: 'tss', label: 'TSS', unit: 'mg/L', step: 1, hint: 'Total suspended solids' },
    { key: 'color', label: 'Colour', unit: 'Pt-Co', step: 1, hint: 'Apparent colour' },
    { key: 'oilGrease', label: 'Oil & Grease', unit: 'mg/L', step: 0.1, hint: 'O&G content' },
    { key: 'freeOil', label: 'Free Oil', unit: 'mg/L', step: 0.1, hint: 'Unemulsified oil' },
  ]},
  { key: 'physical', label: 'Physical', icon: Thermometer, color: '#f87171', params: [
    { key: 'turbidity', label: 'Turbidity', unit: 'NTU', step: 0.1, hint: 'Cloudiness' },
    { key: 'temperature', label: 'Temperature', unit: '°C', step: 0.1, hint: 'Physical temp reading' },
  ]},
  { key: 'chemical', label: 'Chemical', icon: FlaskConical, color: '#a78bfa', params: [
    { key: 'ph', label: 'pH', unit: '', step: 0.01, hint: 'For chemical balance' },
    { key: 'alkalinity', label: 'Alkalinity', unit: 'mg/L CaCO₃', step: 1, hint: 'Buffering capacity' },
    { key: 'chlorides', label: 'Chlorides', unit: 'mg/L Cl', step: 1, hint: 'Salt content' },
  ]},
  { key: 'nutrients', label: 'Nutrients', icon: Atom, color: '#fb923c', params: [
    { key: 'ammoniaN', label: 'Ammonia-N', unit: 'mg/L', step: 0.1, hint: 'NH₃-N concentration' },
    { key: 'totalNitrogen', label: 'Total Nitrogen', unit: 'mg/L N', step: 0.1, hint: 'All nitrogen forms' },
    { key: 'totalPhosphorus', label: 'Total Phosphorus', unit: 'mg/L P', step: 0.01, hint: 'All phosphorus forms' },
    { key: 'tkn', label: 'TKN', unit: 'mg/L N', step: 0.1, hint: 'Total Kjeldahl nitrogen' },
  ]},
  { key: 'metals', label: 'Heavy Metals', icon: ShieldAlert, color: '#f43f5e', params: [
    { key: 'lead', label: 'Lead', unit: 'mg/L Pb', step: 0.001, hint: 'Pb concentration' },
    { key: 'mercury', label: 'Mercury', unit: 'mg/L Hg', step: 0.0001, hint: 'Hg concentration' },
    { key: 'cadmium', label: 'Cadmium', unit: 'mg/L Cd', step: 0.001, hint: 'Cd concentration' },
    { key: 'chromium', label: 'Chromium', unit: 'mg/L Cr', step: 0.001, hint: 'Cr concentration' },
    { key: 'arsenic', label: 'Arsenic', unit: 'mg/L As', step: 0.001, hint: 'As concentration' },
    { key: 'nickel', label: 'Nickel', unit: 'mg/L Ni', step: 0.001, hint: 'Ni concentration' },
    { key: 'zinc', label: 'Zinc', unit: 'mg/L Zn', step: 0.01, hint: 'Zn concentration' },
    { key: 'copper', label: 'Copper', unit: 'mg/L Cu', step: 0.01, hint: 'Cu concentration' },
  ]},
  { key: 'other', label: 'Other', icon: Activity, color: '#38bdf8', params: [
    { key: 'sulfides', label: 'Sulfides', unit: 'mg/L S', step: 0.01, hint: 'S²⁻ concentration' },
    { key: 'phenol', label: 'Phenol', unit: 'mg/L', step: 0.01, hint: 'Phenolic compounds' },
    { key: 'surfactants', label: 'Surfactants', unit: 'mg/L', step: 0.1, hint: 'Detergent content' },
  ]},
] as const;

/* ─── Chemical active-fraction lookup (product purity as-supplied) ─── */
const CHEMICAL_PURITY: Record<string, number> = {
  fecl3:    0.38,   // 38% w/w FeCl3 in commercial 40% solution
  alum:     0.17,   // ~17% active Al2O3 equivalent in liquid alum
  pac:      0.10,   // ~10% Al2O3 equivalent in PAC solution
  feso4:    0.20,   // ~20% FeSO4 in ferrous sulphate solution
  lime:     0.90,   // 90% CaO equivalent in hydrated lime
  polymer:  1.00,   // Dry polymer — 100% active
  naclo:    0.12,   // 12% NaOCl in sodium hypochlorite solution
  caustic_soda: 0.30, // 30% NaOH solution
};

/* ─── KPI severity helpers ─── */
type KpiLimits = { tss?: number; bod5?: number; cod?: number; phMin?: number; phMax?: number; oilGrease?: number; [k: string]: number | undefined };

function getKpiStatus(key: string, value: number, limits?: KpiLimits): { status: 'good' | 'warn' | 'critical'; label: string } {
  if (!value || value === 0) return { status: 'good', label: '—' };
  switch (key) {
    case 'bod5': {
      const lim = limits?.bod5 ?? 25;
      return value <= lim ? { status: 'good', label: 'Compliant' }
           : value <= lim * 10 ? { status: 'warn', label: `>${lim} mg/L` }
           : { status: 'critical', label: 'Very High' };
    }
    case 'cod': {
      const lim = limits?.cod ?? 125;
      return value <= lim ? { status: 'good', label: 'Compliant' }
           : value <= lim * 8 ? { status: 'warn', label: `>${lim} mg/L` }
           : { status: 'critical', label: 'Very High' };
    }
    case 'tss': {
      const lim = limits?.tss ?? 35;
      return value <= lim ? { status: 'good', label: 'Compliant' }
           : value <= lim * 10 ? { status: 'warn', label: `>${lim} mg/L` }
           : { status: 'critical', label: 'Very High' };
    }
    case 'ph': {
      const lo = limits?.phMin ?? 6.0, hi = limits?.phMax ?? 9.0;
      return value >= lo && value <= hi ? { status: 'good', label: 'Optimal' }
           : (value >= lo - 0.5 && value < lo) || (value > hi && value <= hi + 0.5) ? { status: 'warn', label: 'Marginal' }
           : { status: 'critical', label: 'Extreme' };
    }
    case 'conductivity':
      return value < 1500 ? { status: 'good', label: 'Fresh' }
           : value <= 5000 ? { status: 'warn', label: 'Brackish' }
           : { status: 'critical', label: 'Saline' };
    case 'oilGrease': {
      const lim = limits?.oilGrease ?? 15;
      return value <= lim ? { status: 'good', label: 'Compliant' }
           : value <= lim * 4 ? { status: 'warn', label: `>${lim} mg/L` }
           : { status: 'critical', label: 'Very High' };
    }
    default: return { status: 'good', label: 'Normal' };
  }
}

const STATUS_COLORS = {
  good: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', glow: 'shadow-emerald-500/10', dot: 'bg-emerald-400' },
  warn: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', glow: 'shadow-amber-500/10', dot: 'bg-amber-400' },
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400', glow: 'shadow-red-500/10', dot: 'bg-red-400' },
};

/* ═══════════════════════════════════════════
   MAIN PARAMETERS PAGE — ENTERPRISE DARK
   ═══════════════════════════════════════════ */

export default function ParametersPage() {
  const navigate = useNavigate();
  const calc = useCalculator();
  const { requireAuth, AuthModal } = useRequireAuth();
  const { isAuthenticated, isGuest, isAdmin } = useAuth();
  const [activeCat, setActiveCat] = useState<string>('basic');
  const [showResults, setShowResults] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [sampleName, setSampleName] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [savedSamples, setSavedSamples] = useState<CustomerSample[]>(getLocalCustSamples);
  const [dairyOpen, setDairyOpen] = useState(false);
  const [breweryOpen, setBreweryOpen] = useState(false);
  const [dosingPopupOpen, setDosingPopupOpen] = useState(false);

  /* Seed customers if none */
  const customers = useMemo(() => {
    const existing = getLocalCustomers();
    if (existing.length > 0) return existing;
    const demos: Customer[] = [
      { id: 1, name: 'Windsor Dairy Ltd', company: 'Windsor Food Group' },
      { id: 2, name: 'Thames Brewing Co.', company: 'Thames Craft Ales Ltd' },
      { id: 3, name: 'Sheffield Metalworks', company: 'South Yorkshire Industrial Plc' },
    ];
    localStorage.setItem('rb_customers', JSON.stringify(demos));
    return demos;
  }, [saveDialogOpen]);

  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const q = customerSearch.toLowerCase();
    return customers.filter(c =>
      c.name.toLowerCase().includes(q) || (c.company && c.company.toLowerCase().includes(q))
    );
  }, [customers, customerSearch]);

  /* ─── Stats ─── */
  const filledCount = useMemo(() => Object.values(calc.params).filter(v => typeof v === 'number' && v > 0).length, [calc.params]);
  const totalParams = new Set(CATEGORIES.flatMap(c => c.params.map(p => p.key))).size;
  const completionPct = Math.round((filledCount / totalParams) * 100);

  const categoryFill = useMemo(() => {
    return CATEGORIES.map(cat => ({
      key: cat.key,
      label: cat.label,
      filled: cat.params.filter(p => (calc.params as Record<string, number>)[p.key] > 0).length,
      total: cat.params.length,
      color: cat.color,
    }));
  }, [calc.params]);

  /* ─── KPI data ─── */
  const kpiData = useMemo(() => {
    const p = calc.params;
    return [
      { key: 'bod5', label: 'BOD₅', value: p.bod5, unit: 'mg/L', icon: Droplets },
      { key: 'cod', label: 'COD', value: p.cod, unit: 'mg/L', icon: FlaskConical },
      { key: 'tss', label: 'TSS', value: p.tss, unit: 'mg/L', icon: Layers },
      { key: 'ph', label: 'pH', value: p.ph, unit: '', icon: Beaker },
      { key: 'conductivity', label: 'Conductivity', value: p.conductivity, unit: 'µS/cm', icon: Zap },
      { key: 'oilGrease', label: 'O&G', value: p.oilGrease, unit: 'mg/L', icon: Wind },
    ];
  }, [calc.params]);

  /* ─── BOD/COD ratio ─── */
  const bodCodRatio = useMemo(() => {
    const { bod5, cod } = calc.params;
    if (!bod5 || !cod || cod === 0) return null;
    return { ratio: bod5 / cod, bod5, cod };
  }, [calc.params]);

  /* ─── DO saturation at temperature ─── */
  const doSaturation = useMemo(() => {
    const t = calc.params.temperature;
    if (!t || t <= 0) return null;
    // Benson & Krause equation (simplified): DO_sat (mg/L) at temperature T°C
    const doSat = parseFloat((14.62 - 0.3898 * t + 0.006969 * t * t - 0.00005896 * t * t * t).toFixed(2));
    const viability = doSat >= 7 ? 'Excellent' : doSat >= 5 ? 'Good' : doSat >= 3 ? 'Marginal' : 'Poor';
    return { temp: t, doSat, viability };
  }, [calc.params.temperature]);

  /* ─── Saline mode detection ─── */
  const isSaline = useMemo(() => {
    const { conductivity, tds } = calc.params;
    return (conductivity > 5000 || tds > 5000);
  }, [calc.params.conductivity, calc.params.tds]);

  /* ─── Radar chart data ─── */
  const radarData = useMemo(() => {
    const p = calc.params;
    // Adaptive max: max(entered_value × 1.5, typical_industrial_max)
    // This prevents clipping — values beyond typical max show relatively
    const adaptMax = (val: number, typicalMax: number) => Math.max(typicalMax, val * 1.5 || typicalMax);
    const maxBod    = adaptMax(p.bod5,        2000);
    const maxCod    = adaptMax(p.cod,         5000);
    const maxTss    = adaptMax(p.tss,         2000);
    const maxCond   = adaptMax(p.conductivity, 10000);
    const maxOG     = adaptMax(p.oilGrease,    500);
    const maxNH3    = adaptMax(p.ammoniaN,     500);
    return [
      { param: 'BOD', full: 'BOD₅',         value: maxBod   > 0 ? Math.min(100, (p.bod5        / maxBod)   * 100) : 0, raw: p.bod5,        max: maxBod   },
      { param: 'COD', full: 'COD',           value: maxCod   > 0 ? Math.min(100, (p.cod         / maxCod)   * 100) : 0, raw: p.cod,         max: maxCod   },
      { param: 'TSS', full: 'TSS',           value: maxTss   > 0 ? Math.min(100, (p.tss         / maxTss)   * 100) : 0, raw: p.tss,         max: maxTss   },
      { param: 'pH',  full: 'pH',            value:              Math.min(100, (p.ph          / 14)    * 100),          raw: p.ph,          max: 14       },
      { param: 'Cond', full: 'Conductivity', value: maxCond  > 0 ? Math.min(100, (p.conductivity / maxCond)  * 100) : 0, raw: p.conductivity, max: maxCond  },
      { param: 'O&G', full: 'Oil & Grease',  value: maxOG    > 0 ? Math.min(100, (p.oilGrease   / maxOG)    * 100) : 0, raw: p.oilGrease,   max: maxOG    },
      { param: 'NH₃', full: 'Ammonia',       value: maxNH3   > 0 ? Math.min(100, (p.ammoniaN   / maxNH3)   * 100) : 0, raw: p.ammoniaN,    max: maxNH3   },
    ];
  }, [calc.params]);

  /* ─── Actions ─── */
  const handleRun = useCallback(() => {
    calc.runAssessment();
    setShowResults(true);
    // Check if dosing popup should appear after assessment
    setTimeout(() => {
      const hasDosingReqs = calc.result?.equipmentDosingRequirements && calc.result.equipmentDosingRequirements.length > 0;
      const hasDosingWarning = calc.result?.dosingWarning?.show;
      if (hasDosingReqs || hasDosingWarning) {
        setDosingPopupOpen(true);
      }
    }, 800);
    toast.success('Assessment completed — treatment recommendations generated');
  }, [calc]);

  const handleApplyDosing = useCallback((newDosings: import('@/types/calculator').DosingConfig[]) => {
    // Replace all dosings with the applied set
    newDosings.forEach((d, i) => {
      if (i < calc.dosingConfig.length) {
        calc.updateDosing(i, d);
      } else {
        calc.addDosing();
        calc.updateDosing(calc.dosingConfig.length - 1, d);
      }
    });
    // Mark dosing as acknowledged so warning doesn't reappear
    calc.acknowledgeDosing();
    setDosingPopupOpen(false);
    toast.success('Dosing updated — re-running assessment with new chemicals');
    setTimeout(() => calc.runAssessment(), 100);
  }, [calc]);

  const handleReset = useCallback(() => {
    calc.resetParams();
    setShowResults(false);
    toast.info('Parameters reset to defaults');
  }, [calc]);

  const handleSave = useCallback(() => {
    if (!requireAuth()) return;
    if (!selectedCustomerId) { toast.error('Select a customer'); return; }
    if (!sampleName.trim()) { toast.error('Enter a sample name'); return; }
    const customer = customers.find(c => c.id === selectedCustomerId);
    if (!customer) { toast.error('Customer not found'); return; }
    const list = getLocalCustSamples();
    const newSample: CustomerSample = {
      id: genLocalId(list), customerId: selectedCustomerId,
      sampleName: sampleName.trim(), params: { ...calc.params } as Record<string, number>,
      standard: calc.activeStandard, createdAt: new Date().toISOString(),
      assessmentResult: calc.hasCalculated ? (calc.result as unknown as Record<string, unknown>) : null,
    };
    list.unshift(newSample);
    setLocalCustSamples(list); setSavedSamples(list);
    toast.success(`Saved "${sampleName.trim()}" to ${customer.name}`);
    setSaveDialogOpen(false); setSampleName('');
  }, [selectedCustomerId, sampleName, customers, calc.params, calc.activeStandard, calc.hasCalculated, calc.result, requireAuth]);

  const handleDeleteSample = useCallback((id: number) => {
    if (!requireAuth()) return;
    const sample = getLocalCustSamples().find(s => s.id === id);
    if (sample) {
      const policy = getRetentionPolicy(sample.standard ?? undefined);
      if (isWithinRetention(sample.createdAt, policy)) {
        if (!isAdmin) {
          toast.error(retentionToastMessage(policy, sample.createdAt));
          return;
        }
        if (!window.confirm(adminOverridePrompt(policy, sample.createdAt, 'sample'))) return;
        const list = getLocalCustSamples().filter(s => s.id !== id);
        setLocalCustSamples(list); setSavedSamples(list);
        toast.warning('Sample deleted (admin override — within retention period)');
        return;
      }
    }
    const list = getLocalCustSamples().filter(s => s.id !== id);
    setLocalCustSamples(list); setSavedSamples(list);
    toast.info('Sample deleted');
  }, [requireAuth, isAdmin]);

  const hasTrend = useMemo(() => {
    const counts: Record<number, number> = {};
    for (const s of savedSamples) counts[s.customerId] = (counts[s.customerId] || 0) + 1;
    return Object.values(counts).some(c => c >= 3);
  }, [savedSamples]);

  /* ─── Interdependency validation warnings ─── */
  const validationWarnings = useMemo(() => {
    const p = calc.params;
    const warns: string[] = [];
    // BOD > COD is physically impossible
    if (p.bod5 > 0 && p.cod > 0 && p.bod5 > p.cod) {
      warns.push(`BOD₅ (${p.bod5} mg/L) exceeds COD (${p.cod} mg/L) — physically impossible. BOD₅ must always be ≤ COD.`);
    }
    // BOD/COD ratio <0.1 suggests non-biodegradable wastewater — just informational
    if (p.bod5 > 0 && p.cod > 0 && (p.bod5 / p.cod) < 0.1) {
      warns.push(`BOD₅/COD ratio is ${(p.bod5 / p.cod).toFixed(3)} (<0.1) — may indicate non-biodegradable or toxic wastewater.`);
    }
    // Free oil > total oil & grease
    if (p.freeOil > 0 && p.oilGrease > 0 && p.freeOil > p.oilGrease) {
      warns.push(`Free Oil (${p.freeOil} mg/L) exceeds Total O&G (${p.oilGrease} mg/L) — Free Oil is a fraction of total O&G.`);
    }
    // TKN > Total Nitrogen
    if (p.tkn > 0 && p.totalNitrogen > 0 && p.tkn > p.totalNitrogen) {
      warns.push(`TKN (${p.tkn} mg/L) exceeds Total Nitrogen (${p.totalNitrogen} mg/L) — TKN is a subset of Total N.`);
    }
    // TDS/Conductivity consistency (expected ratio 0.4–0.8 for fresh water)
    if (p.tds > 0 && p.conductivity > 0) {
      const ratio = p.tds / p.conductivity;
      if (ratio < 0.35 || ratio > 0.85) {
        warns.push(`TDS/Conductivity ratio is ${ratio.toFixed(2)} (expected 0.4–0.8 for typical wastewater) — check measurement units or data entry.`);
      }
    }
    // Very low pH with high alkalinity is chemically inconsistent
    if (p.ph > 0 && p.ph < 5 && p.alkalinity > 100) {
      warns.push(`pH ${p.ph.toFixed(1)} with alkalinity ${p.alkalinity} mg/L CaCO₃ is chemically inconsistent — low pH destroys carbonate alkalinity.`);
    }
    return warns;
  }, [calc.params]);

  /* ─── Render helpers ─── */
  const currentCat = CATEGORIES.find(c => c.key === activeCat)!;

  return (
    <div className={`space-y-6 ${isSaline ? 'ring-1 ring-amber-500/30 rounded-2xl p-1' : ''}`}>
      {/* Saline mode warning */}
      {isSaline && (
        <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs">
          <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400" />
          <span><strong>Saline Wastewater Detected</strong> — Conductivity or TDS exceeds 5000 mg/L. Standard biological treatment recommendations may be invalid. Consider electrocoagulation or membrane-based alternatives.</span>
        </div>
      )}
      {/* Page Title */}
      <PageHeader icon={Activity} title="Water Quality Parameters" subtitle="Enter and analyse influent water quality parameters for treatability assessment" />

      {/* ═════ KPI HEADER CARDS ═════ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpiData.map((kpi, i) => {
          const status = getKpiStatus(kpi.key, kpi.value, calc.limits as KpiLimits);
          const colors = STATUS_COLORS[status.status];
          const Icon = kpi.icon;
          return (
            <motion.div
              key={kpi.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`rb-card p-4 border ${colors.border} ${colors.bg} hover:shadow-lg ${colors.glow} transition-all cursor-pointer group`}
              onClick={() => {
                const cat = CATEGORIES.find(c => c.params.some(p => p.key === kpi.key));
                if (cat) setActiveCat(cat.key);
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`w-8 h-8 rounded-lg ${colors.bg} border ${colors.border} flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${colors.text}`} />
                </div>
                <div className={`w-2 h-2 rounded-full ${colors.dot} ${status.status === 'critical' ? 'animate-pulse' : ''}`} />
              </div>
              <p className="text-2xl font-bold text-white tabular-nums">{kpi.value > 0 ? kpi.value.toLocaleString('en-GB', { maximumFractionDigits: 2 }) : '—'}</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-slate-400">{kpi.label}</p>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>{status.label}</span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* BOD/COD ratio indicator */}
      {bodCodRatio && (
        <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border text-xs ${
          bodCodRatio.ratio > 1.0
            ? 'bg-red-500/10 border-red-500/25 text-red-300'
            : bodCodRatio.ratio < 0.1
            ? 'bg-amber-500/10 border-amber-500/25 text-amber-300'
            : 'bg-slate-800/50 border-slate-800/40 text-slate-400'
        }`}>
          <FlaskConical className="w-3.5 h-3.5 shrink-0" />
          <span>
            BOD₅/COD ratio: <strong className="text-white">{bodCodRatio.ratio.toFixed(3)}</strong>
            {bodCodRatio.ratio > 1.0
              ? ' ⚠ Impossible — BOD₅ cannot exceed COD. Check data entry.'
              : bodCodRatio.ratio < 0.1
              ? ' ⚠ Ratio <0.1 — very low biodegradability. May be toxic or heavily industrial.'
              : bodCodRatio.ratio < 0.3
              ? ' — Low biodegradability. Chemical treatment likely required.'
              : bodCodRatio.ratio < 0.6
              ? ' — Moderate biodegradability. Combined biological + chemical recommended.'
              : ' — Good biodegradability. Biological treatment viable.'}
          </span>
        </div>
      )}

      {/* ── Validation warnings ── */}
      {validationWarnings.length > 0 && (
        <div className="space-y-2">
          {validationWarnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2.5 px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-300 text-xs">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* ═════ MAIN WORKSPACE ═════ */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* LEFT: Parameter Entry */}
        <div className="xl:col-span-8 space-y-5">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => {
              const fill = categoryFill.find(c => c.key === cat.key)!;
              const isActive = activeCat === cat.key;
              const Icon = cat.icon;
              return (
                <button
                  key={cat.key}
                  onClick={() => setActiveCat(cat.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all border ${
                    isActive
                      ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400 shadow-lg shadow-cyan-500/5'
                      : 'border-slate-800/50 bg-slate-800/30 text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                  }`}
                >
                  <Icon className="w-4 h-4" style={{ color: isActive ? cat.color : undefined }} />
                  <span className="hidden sm:inline">{cat.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${fill.filled === fill.total ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'}`}>
                    {fill.filled}/{fill.total}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Parameter Grid */}
          <motion.div
            key={activeCat}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25 }}
            className="rb-card p-5"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${currentCat.color}15`, border: `1px solid ${currentCat.color}30` }}>
                <currentCat.icon className="w-5 h-5" style={{ color: currentCat.color }} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">{currentCat.label}</h3>
                <p className="text-xs text-slate-500">{currentCat.params.length} parameters</p>
              </div>
              <div className="ml-auto">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                  categoryFill.find(c => c.key === activeCat)!.filled === currentCat.params.length
                    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
                    : 'bg-slate-800 text-slate-500 border border-slate-700'
                }`}>
                  {categoryFill.find(c => c.key === activeCat)!.filled} / {currentCat.params.length} filled
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {currentCat.params.map(p => {
                const val = (calc.params as Record<string, number>)[p.key] || 0;
                const isFilled = val > 0;
                return (
                  <div
                    key={p.key}
                    className={`group relative p-3 rounded-xl border transition-all ${
                      isFilled
                        ? 'bg-slate-800/60 border-slate-600/50 shadow-sm'
                        : 'bg-slate-900/30 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-slate-300">{p.label}</label>
                      <div className="flex items-center gap-1">
                        {isFilled && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                        <span className="text-[10px] text-slate-600 font-medium">{p.unit}</span>
                      </div>
                    </div>
                    <input
                      type="number"
                      step={p.step}
                      value={isFilled ? val : ''}
                      onChange={e => {
                        const v = e.target.value === '' ? 0 : parseFloat(e.target.value);
                        calc.updateParam(p.key as keyof typeof calc.params, isNaN(v) ? 0 : v);
                      }}
                      placeholder="0"
                      className={`w-full px-3 py-2.5 rounded-lg border text-sm font-medium transition-all outline-none focus:ring-2 ${
                        isFilled
                          ? 'bg-slate-800 border-slate-600 text-white focus:border-cyan-400 focus:ring-cyan-400/20'
                          : 'bg-slate-900/50 border-slate-700 text-slate-300 placeholder-slate-600 focus:border-cyan-400/50 focus:ring-cyan-400/10'
                      }`}
                    />
                    <p className="text-[10px] text-slate-600 mt-1">{p.hint}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Dairy / Brewery Toggles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Dairy */}
            <div className={`rb-card p-4 border transition-all ${calc.dairy.enabled ? 'border-blue-500/30 bg-blue-500/5' : 'border-slate-800'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${calc.dairy.enabled ? 'bg-blue-500/20' : 'bg-slate-800'}`}>
                    <Milk className={`w-5 h-5 ${calc.dairy.enabled ? 'text-blue-400' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white">Dairy Effluent</h4>
                    <p className="text-xs text-slate-500">Milk processing wastewater blend</p>
                  </div>
                </div>
                <button
                  onClick={() => calc.updateDairy('enabled', !calc.dairy.enabled)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${calc.dairy.enabled ? 'bg-blue-500' : 'bg-slate-700'}`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-rb-surface shadow transition-transform ${calc.dairy.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <AnimatePresence>
                {calc.dairy.enabled && (
                  <motion.div
                    key="dairy-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 pt-4 border-t border-slate-800 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[
                        { key: 'flowRate', label: 'Flow Rate', unit: 'm³/d', step: 0.1 },
                        { key: 'bod5', label: 'BOD₅', unit: 'mg/L', step: 1 },
                        { key: 'cod', label: 'COD', unit: 'mg/L', step: 1 },
                        { key: 'tss', label: 'TSS', unit: 'mg/L', step: 1 },
                        { key: 'oilGrease', label: 'O&G', unit: 'mg/L', step: 0.1 },
                        { key: 'ph', label: 'pH', unit: '', step: 0.01 },
                        { key: 'tds', label: 'TDS', unit: 'mg/L', step: 1 },
                        { key: 'totalNitrogen', label: 'Total N', unit: 'mg/L', step: 0.1 },
                        { key: 'totalPhosphorus', label: 'Total P', unit: 'mg/L', step: 0.01 },
                        { key: 'temperature', label: 'Temp', unit: '°C', step: 0.1 },
                        { key: 'fatContent', label: 'Fat', unit: 'mg/L', step: 1 },
                        { key: 'lactose', label: 'Lactose', unit: 'mg/L', step: 1 },
                      ].map(f => (
                        <div key={`d-${f.key}`}>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">{f.label} <span className="text-slate-600">({f.unit})</span></label>
                          <input
                            type="number"
                            step={f.step}
                            value={(calc.dairy as Record<string, number>)[f.key] || ''}
                            onChange={e => calc.updateDairy(f.key as keyof typeof calc.dairy, parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 rounded-lg border border-slate-700 bg-slate-900/50 text-sm text-white focus:border-blue-400 focus:ring-1 focus:ring-blue-400/20 outline-none"
                          />
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Brewery */}
            <div className={`rb-card p-4 border transition-all ${calc.brewery.enabled ? 'border-amber-500/30 bg-amber-500/5' : 'border-slate-800'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${calc.brewery.enabled ? 'bg-amber-500/20' : 'bg-slate-800'}`}>
                    <Beer className={`w-5 h-5 ${calc.brewery.enabled ? 'text-amber-400' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white">Brewery Effluent</h4>
                    <p className="text-xs text-slate-500">Brewing process wastewater blend</p>
                  </div>
                </div>
                <button
                  onClick={() => calc.updateBrewery('enabled', !calc.brewery.enabled)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${calc.brewery.enabled ? 'bg-amber-500' : 'bg-slate-700'}`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-rb-surface shadow transition-transform ${calc.brewery.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <AnimatePresence>
                {calc.brewery.enabled && (
                  <motion.div
                    key="brewery-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 pt-4 border-t border-slate-800 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[
                        { key: 'flowRate', label: 'Flow Rate', unit: 'm³/d', step: 0.1 },
                        { key: 'bod5', label: 'BOD₅', unit: 'mg/L', step: 1 },
                        { key: 'cod', label: 'COD', unit: 'mg/L', step: 1 },
                        { key: 'tss', label: 'TSS', unit: 'mg/L', step: 1 },
                        { key: 'ph', label: 'pH', unit: '', step: 0.01 },
                        { key: 'tds', label: 'TDS', unit: 'mg/L', step: 1 },
                        { key: 'totalNitrogen', label: 'Total N', unit: 'mg/L', step: 0.1 },
                        { key: 'totalPhosphorus', label: 'Total P', unit: 'mg/L', step: 0.01 },
                        { key: 'temperature', label: 'Temp', unit: '°C', step: 0.1 },
                        { key: 'alcoholContent', label: 'Alcohol', unit: '%', step: 0.1 },
                        { key: 'hopsResidue', label: 'Hops', unit: 'mg/L', step: 1 },
                        { key: 'yeastCount', label: 'Yeast', unit: 'CFU/mL', step: 1 },
                      ].map(f => (
                        <div key={`b-${f.key}`}>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">{f.label} <span className="text-slate-600">({f.unit})</span></label>
                          <input
                            type="number"
                            step={f.step}
                            value={(calc.brewery as Record<string, number>)[f.key] || ''}
                            onChange={e => calc.updateBrewery(f.key as keyof typeof calc.brewery, parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 rounded-lg border border-slate-700 bg-slate-900/50 text-sm text-white focus:border-amber-400 focus:ring-1 focus:ring-amber-400/20 outline-none"
                          />
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Blended Summary */}
          {calc.blended && (calc.dairy.enabled || calc.brewery.enabled) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rb-card p-4 border border-slate-700"
            >
              <div className="flex items-center gap-3 mb-3">
                <Layers className="w-5 h-5 text-rb-brand" />
                <h4 className="text-sm font-semibold text-white">Blended Wastewater Profile</h4>
                <span className="text-xs text-slate-500 ml-auto">{calc.blended.label}</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {calc.blended.contributions.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700">
                    <div className="w-2 h-2 rounded-full" style={{ background: i === 0 ? '#22d3ee' : i === 1 ? '#60a5fa' : '#fbbf24' }} />
                    <span className="text-xs text-slate-300">{c.name}</span>
                    <span className="text-xs font-bold text-white">{c.percentage}%</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {[
                  { label: 'BOD₅', val: calc.blended.params.bod5, unit: 'mg/L' },
                  { label: 'COD', val: calc.blended.params.cod, unit: 'mg/L' },
                  { label: 'TSS', val: calc.blended.params.tss, unit: 'mg/L' },
                  { label: 'pH', val: calc.blended.params.ph, unit: '' },
                ].map(m => (
                  <div key={m.label} className="p-2 rounded-lg bg-slate-900/50 border border-slate-800">
                    <span className="text-slate-500 block">{m.label}</span>
                    <span className="text-white font-bold">{m.val.toLocaleString('en-GB', { maximumFractionDigits: 1 })}{m.unit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Action Bar */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button onClick={handleReset} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-700 text-slate-400 hover:text-white hover:border-slate-600 transition-all">
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
              <button onClick={() => setSaveDialogOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-700 text-slate-300 hover:text-white hover:border-rb-brand/50 hover:bg-rb-brand/5 transition-all">
                <Save className="w-4 h-4" /> Save Sample
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2">
                <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
                  <motion.div className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400 rounded-full" initial={{ width: 0 }} animate={{ width: `${completionPct}%` }} />
                </div>
                <span className="text-xs text-slate-500">{completionPct}%</span>
              </div>
              <button
                onClick={handleRun}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all shadow-lg shadow-[#cda42e]/10"
              >
                <Sparkles className="w-4 h-4" /> Influent Sample Assessment
              </button>
            </div>
          </div>

          {/* Results Section */}
          <AnimatePresence>
            {showResults && calc.hasCalculated && calc.result && (
              <motion.div
                key="results-panel"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="space-y-4"
              >
                {/* Results header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                      <Target className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white">Assessment Results</h3>
                      <p className="text-xs text-slate-500">
                        TI: {calc.result.overallTreatabilityIndex} · {calc.result.treatabilityClassification}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSaveDialogOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all"
                  >
                    <Save className="w-4 h-4" /> Save with Customer
                  </button>
                </div>

                {/* ResultsTabs */}
                <div className="rb-card overflow-hidden">
                  <ResultsTabs
                    result={calc.result}
                    blended={calc.blended}
                    limits={calc.limits}
                    samples={calc.samples}
                    hasSamplesChart={calc.samples.length >= 2}
                    params={calc.params}
                    dosingConfig={calc.dosingConfig}
                    activeStandard={calc.activeStandard}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* RIGHT: Charts + Info Panel */}
        <div className="xl:col-span-4 space-y-4">
          {/* Radar Chart */}
          <div className="rb-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileBarChart className="w-4 h-4 text-rb-brand" />
              <h4 className="text-sm font-semibold text-white">Parameter Profile</h4>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#334155" />
                  <PolarAngleAxis dataKey="param" tick={{ fill: 'var(--rb-text-2)', fontSize: 10 }} />
                  <PolarRadiusAxis tick={{ fill: 'var(--rb-text-3)', fontSize: 9 }} domain={[0, 100]} />
                  <Radar name="Current" dataKey="value" stroke="#22d3ee" fill="#22d3ee" fillOpacity={0.15} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* DO Saturation indicator */}
          {doSaturation && (
            <div className="rb-card p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Thermometer className="w-4 h-4 text-cyan-400" />
                  Biological Viability Preview
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2.5 rounded-lg bg-slate-800/50 border border-slate-800/40 text-center">
                  <p className="text-[10px] text-slate-500 mb-1">Temperature</p>
                  <p className="text-sm font-bold text-white">{doSaturation.temp.toFixed(1)} °C</p>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-800/50 border border-slate-800/40 text-center">
                  <p className="text-[10px] text-slate-500 mb-1">DO Saturation</p>
                  <p className={`text-sm font-bold ${doSaturation.doSat >= 7 ? 'text-emerald-400' : doSaturation.doSat >= 5 ? 'text-cyan-400' : doSaturation.doSat >= 3 ? 'text-amber-400' : 'text-red-400'}`}>
                    {doSaturation.doSat} mg/L
                  </p>
                </div>
              </div>
              <div className="mt-2 px-2.5 py-1.5 rounded-lg bg-slate-800/30 border border-slate-800/30">
                <p className="text-[10px] text-slate-500">Biological viability: <span className={`font-bold ${doSaturation.viability === 'Excellent' ? 'text-emerald-400' : doSaturation.viability === 'Good' ? 'text-cyan-400' : doSaturation.viability === 'Marginal' ? 'text-amber-400' : 'text-red-400'}`}>{doSaturation.viability}</span></p>
                {doSaturation.doSat < 5 && (
                  <p className="text-[10px] text-amber-400 mt-0.5">⚠ Low DO saturation may inhibit aerobic biological processes at this temperature</p>
                )}
              </div>
            </div>
          )}

          {/* Completion Rings */}
          <div className="rb-card p-4">
            <h4 className="text-sm font-semibold text-white mb-3">Category Completion</h4>
            <div className="space-y-3">
              {categoryFill.map(cat => {
                const pct = Math.round((cat.filled / cat.total) * 100);
                return (
                  <div key={cat.key} className="flex items-center gap-3">
                    <div className="w-24 shrink-0">
                      <span className="text-xs text-slate-400">{cat.label}</span>
                    </div>
                    <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: cat.color }}
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                    <span className="text-xs text-slate-500 w-10 text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Treatability Preview */}
          {calc.result && (
            <div className="rb-card p-4 border border-slate-700">
              <h4 className="text-sm font-semibold text-white mb-3">Treatability Preview</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 rounded-lg bg-slate-800/50">
                  <span className="text-xs text-slate-400">Classification</span>
                  <span className="text-xs font-bold text-cyan-400">{calc.result.treatabilityClassification}</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded-lg bg-slate-800/50">
                  <span className="text-xs text-slate-400">Biological Viability</span>
                  <span className="text-xs font-bold text-emerald-400">
                    {calc.result.biologicalViabilityScore}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 rounded-lg bg-slate-800/50">
                  <span className="text-xs text-slate-400">Toxic Parameters</span>
                  <span className={`text-xs font-bold ${calc.result.toxicParametersCount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {calc.result.toxicParametersCount} found
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 rounded-lg bg-slate-800/50">
                  <span className="text-xs text-slate-400">Top Process</span>
                  <span className="text-xs font-bold text-rb-brand">
                    {calc.result.processCompatibility?.[0]?.process || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Active standard limits reference */}
          {(() => {
            const std = ALL_STANDARDS.find(s => s.code === calc.activeStandard);
            if (!std) return null;
            return (
              <div className="rb-card p-4 border border-slate-800/50">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldAlert className="w-4 h-4 text-rb-brand" />
                  <h4 className="text-sm font-semibold text-white">Discharge Limits</h4>
                  <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${
                    calc.activeStandard === 'UK_EA' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                    calc.activeStandard === 'AU_EPA' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                    'bg-violet-500/10 text-violet-400 border-violet-500/20'
                  }`}>{std.shortName}</span>
                </div>
                <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                  {[
                    { label: 'BOD₅',  val: std.limits.bod5,         unit: 'mg/L', param: 'bod5'  },
                    { label: 'COD',   val: std.limits.cod,          unit: 'mg/L', param: 'cod'   },
                    { label: 'TSS',   val: std.limits.tss,          unit: 'mg/L', param: 'tss'   },
                    { label: 'O&G',   val: std.limits.oilGrease,    unit: 'mg/L', param: 'oilGrease' },
                    { label: 'NH₃-N', val: std.limits.ammoniaN,     unit: 'mg/L', param: 'ammoniaN' },
                    { label: 'Total P', val: std.limits.totalPhosphorus, unit: 'mg/L', param: 'totalPhosphorus' },
                    { label: 'pH min', val: std.limits.phMin,       unit: '',     param: null   },
                    { label: 'pH max', val: std.limits.phMax,       unit: '',     param: null   },
                  ].filter(r => r.val !== undefined && r.val !== null).map(r => {
                    const entered = r.param ? (calc.params as Record<string, number>)[r.param] : null;
                    const over = entered !== null && entered !== undefined && entered > 0 && r.val !== undefined && entered > r.val!;
                    return (
                      <div key={r.label} className={`flex items-center justify-between px-2 py-1.5 rounded-lg border ${
                        over ? 'bg-red-500/10 border-red-500/20' : 'bg-slate-800/40 border-slate-800/30'
                      }`}>
                        <span className={over ? 'text-red-400 font-semibold' : 'text-slate-500'}>{r.label}</span>
                        <span className={`font-bold ${over ? 'text-red-400' : 'text-slate-300'}`}>
                          {r.val}{r.unit && ` ${r.unit}`}
                          {over && ' ⚠'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Quick Dosing Adjuster with Impact */}
          {calc.dosingConfig.length > 0 && (
            <div className="rb-card p-4 border border-slate-700">
              <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-rb-brand" />
                Quick Dosing <span className="text-[10px] text-slate-500 font-normal">— adjusts compliance</span>
              </h4>

              {/* Compliance Impact Preview */}
              {calc.result?.simulation && (
                <div className="mb-3 p-2.5 rounded-lg bg-slate-800/40 border border-slate-800/50">
                  <p className="text-[10px] text-slate-500 uppercase font-bold mb-1.5">Compliance Impact Preview</p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {[
                      { label: 'TSS', val: calc.result.simulation.stages[calc.result.simulation.stages.length - 1]?.tss ?? 0, limit: calc.limits.tss, icon: Layers },
                      { label: 'BOD', val: calc.result.simulation.stages[calc.result.simulation.stages.length - 1]?.bod5 ?? 0, limit: calc.limits.bod5, icon: Droplets },
                      { label: 'COD', val: calc.result.simulation.stages[calc.result.simulation.stages.length - 1]?.cod ?? 0, limit: calc.limits.cod, icon: FlaskConical },
                      { label: 'pH', val: calc.result.simulation.stages[calc.result.simulation.stages.length - 1]?.ph ?? 7, limit: `${calc.limits.phMin}-${calc.limits.phMax}`, icon: Beaker },
                    ].map((m, mi) => {
                      const pass = m.label === 'pH'
                        ? (m.val as number) >= calc.limits.phMin && (m.val as number) <= calc.limits.phMax
                        : (m.val as number) <= (typeof m.limit === 'number' ? m.limit : 99999);
                      return (
                        <div key={mi} className={`text-center p-1.5 rounded ${pass ? 'bg-emerald-500/5 border border-emerald-500/10' : 'bg-red-500/5 border border-red-500/10'}`}>
                          <span className="text-[10px] text-slate-500 block">{m.label}</span>
                          <span className={`text-[11px] font-bold ${pass ? 'text-emerald-400' : 'text-red-400'}`}>
                            {typeof m.val === 'number' ? (m.label === 'pH' ? m.val.toFixed(1) : m.val.toFixed(0)) : m.val}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {calc.dosingConfig.map((d, i) => {
                  const chem = AVAILABLE_CHEMICALS.find(c => c.id === d.chemicalId);
                  if (!chem) return null;
                  // Calculate sludge contribution for this chemical
                  const purity = CHEMICAL_PURITY[d.chemicalId] ?? 1.0;
                  const dailyKg = (d.dose * (calc.params.flowRate || 500) * 1000) / 1_000_000 * purity;
                  return (
                    <div key={d.chemicalId} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => calc.updateDosing(i, { enabled: !d.enabled })}
                            className={`w-4 h-4 rounded flex items-center justify-center transition-colors ${
                              d.enabled ? 'bg-cyan-500/20 border border-cyan-500/40' : 'bg-slate-700 border border-slate-600'
                            }`}
                          >
                            {d.enabled && <div className="w-2 h-2 rounded-sm bg-cyan-400" />}
                          </button>
                          <span className={`text-xs font-bold ${d.enabled ? 'text-white' : 'text-slate-500'}`}>{chem.name}</span>
                          {d.enabled && dailyKg > 0.1 && (
                            <span className="text-[10px] text-slate-500 hidden lg:inline">({dailyKg.toFixed(1)} kg/d sludge)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={d.dose}
                            onChange={(e) => calc.updateDosing(i, { dose: parseFloat(e.target.value) || 0 })}
                            className="w-16 px-2 py-1 rounded text-xs text-white bg-slate-800 border border-slate-700 focus:border-rb-brand outline-none text-right"
                            step={chem.id === 'polymer' ? 0.5 : 5}
                            min={0}
                          />
                          <span className="text-[10px] text-slate-500 w-10">{chem.doseUnit}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min={chem.typicalDoseMin}
                          max={chem.typicalDoseMax}
                          step={chem.id === 'polymer' ? 0.5 : 5}
                          value={d.dose}
                          onChange={(e) => calc.updateDosing(i, { dose: parseFloat(e.target.value) })}
                          className="flex-1 h-1.5 rounded-lg appearance-none cursor-pointer"
                          style={{
                            background: `linear-gradient(to right, ${d.enabled ? '#22d3ee' : 'var(--rb-text-4)'} ${(d.dose / chem.typicalDoseMax) * 100}%, #1e293b ${(d.dose / chem.typicalDoseMax) * 100}%)`,
                            outline: 'none',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Sludge summary */}
              {(() => {
                const totalDailyKg = calc.dosingConfig
                  .filter(d => d.enabled)
                  .reduce((sum, d) => sum + (d.dose * (calc.params.flowRate || 500) * 1000) / 1_000_000 * (CHEMICAL_PURITY[d.chemicalId] ?? 1.0), 0);
                if (totalDailyKg < 0.1) return null;
                return (
                  <div className="mt-3 p-2 rounded-lg bg-rb-brand/5 border border-rb-brand/15 flex items-center gap-2">
                    <Info className="w-3 h-3 text-rb-brand shrink-0" />
                    <span className="text-[10px] text-slate-400">
                      Estimated chemical sludge: <strong className="text-rb-brand">{totalDailyKg.toFixed(1)} kg/day</strong> dry solids (purity-adjusted) from chemical precipitation.
                      {totalDailyKg > 50 && ' High sludge — consider thickening + dewatering equipment.'}
                    </span>
                  </div>
                );
              })()}

              <button
                onClick={() => {
                  calc.runAssessment();
                  toast.success('Assessment re-run with updated dosing');
                }}
                className="w-full mt-3 py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all"
              >
                Re-Run Influent Sample Assessment with New Dosing
              </button>
            </div>
          )}

          {/* Saved Samples Mini-List */}
          {savedSamples.length > 0 && (
            <div className="rb-card p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-white">Recent Samples</h4>
                {hasTrend && (
                  <button onClick={() => navigate('/reports')} className="text-[10px] text-rb-brand hover:underline">
                    Trend Report →
                  </button>
                )}
              </div>
              <div className="space-y-2 max-h-48 overflow-auto">
                {savedSamples.slice(0, 5).map(s => {
                  const cust = customers.find(c => c.id === s.customerId);
                  return (
                    <div key={s.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/30 border border-slate-800 hover:border-slate-700 transition-colors">
                      <div className="min-w-0">
                        <p className="text-xs text-slate-300 truncate">{s.sampleName}</p>
                        <p className="text-[10px] text-slate-500">{cust?.name || 'Unknown'} · {new Date(s.createdAt).toLocaleDateString('en-GB')}</p>
                      </div>
                      <button onClick={() => handleDeleteSample(s.id)} className="p-1 text-slate-600 hover:text-red-400 transition-colors shrink-0">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═════ SAVE SAMPLE MODAL ═════ */}
      <AnimatePresence>
        {saveDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSaveDialogOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl border border-slate-700 overflow-hidden flex flex-col max-h-[85vh]"
              style={{ background: 'var(--bg-surface)' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-slate-700 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-rb-brand/10 border border-rb-brand/20 flex items-center justify-center">
                    <Save className="w-5 h-5 text-rb-brand" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white">Save Sample</h3>
                    <p className="text-xs text-slate-500">Link to a customer record</p>
                  </div>
                </div>
                <button onClick={() => setSaveDialogOpen(false)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">×</button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-auto p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Sample Name *</label>
                  <input
                    value={sampleName}
                    onChange={e => setSampleName(e.target.value)}
                    placeholder="e.g. Raw Effluent — Week 1"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-700 bg-slate-800/50 text-white text-sm focus:outline-none focus:ring-2 focus:ring-rb-brand/30 focus:border-rb-brand transition-all"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Select Customer *</label>
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={customerSearch}
                      onChange={e => setCustomerSearch(e.target.value)}
                      placeholder="Search customers..."
                      className="w-full pl-9 pr-4 py-2 rounded-xl border border-slate-700 bg-slate-800/50 text-white text-sm focus:outline-none focus:ring-2 focus:ring-rb-brand/30 focus:border-rb-brand transition-all"
                    />
                  </div>
                  {filteredCustomers.length === 0 ? (
                    <div className="text-center py-6 rounded-xl border border-dashed border-slate-700 bg-slate-800/30">
                      <Building2 className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No customers found</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-56 overflow-auto">
                      {filteredCustomers.map(c => {
                        const isSelected = selectedCustomerId === c.id;
                        const sampleCount = savedSamples.filter(s => s.customerId === c.id).length;
                        return (
                          <button
                            key={c.id}
                            onClick={() => setSelectedCustomerId(c.id)}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                              isSelected ? 'border-rb-brand bg-rb-brand/10' : 'border-slate-700 bg-slate-800/30 hover:bg-slate-800/60'
                            }`}
                          >
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? 'bg-rb-brand' : 'bg-slate-700'}`}>
                              <Users className={`w-4 h-4 ${isSelected ? 'text-slate-200' : 'text-slate-400'}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm font-medium truncate ${isSelected ? 'text-rb-brand' : 'text-slate-200'}`}>{c.name}</p>
                              {c.company && <p className="text-xs text-slate-500 truncate">{c.company}</p>}
                            </div>
                            <span className="text-xs text-slate-500 shrink-0">{sampleCount} sample{sampleCount !== 1 ? 's' : ''}</span>
                            {isSelected && <CheckCircle2 className="w-5 h-5 text-rb-brand shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {selectedCustomerId && (
                  <div className="p-3 rounded-xl bg-rb-brand/5 border border-rb-brand/20">
                    <p className="text-xs text-slate-400">
                      Saving under <strong className="text-rb-brand">{customers.find(c => c.id === selectedCustomerId)?.name}</strong> · Standard: <strong className="text-rb-brand">{calc.activeStandard}</strong>
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-5 border-t border-slate-700 flex items-center justify-between shrink-0 bg-slate-900/30">
                <button onClick={() => setSaveDialogOpen(false)} className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">Cancel</button>
                <button
                  onClick={handleSave}
                  disabled={!selectedCustomerId || !sampleName.trim()}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-md"
                >
                  <Save className="w-4 h-4" /> Save Sample
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═════ DOSING POPUP (after assessment) ═════ */}
      <AnimatePresence>
        {dosingPopupOpen && calc.result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <DosingPopup
              result={calc.result}
              onClose={() => setDosingPopupOpen(false)}
              onApply={handleApplyDosing}
              currentDosings={calc.dosingConfig}
            />
          </motion.div>
        )}
      </AnimatePresence>
      {/* ═════ AUTH MODAL ═════ */}
      <AuthModal />

    </div>
  );
}
